import { useState } from 'react';
import {
  Box,
  Button,
  CloseButton,
  Flex,
  Input,
  Text,
} from '@chakra-ui/react';
import { toast } from 'react-toastify';
import { datadogRum } from '@datadog/browser-rum';
import { capitalizeFirstLetter } from '@utils/formats';
import ErrorLabel from '@components/ErrorLabel';
import api from '@services/api';

interface Props {
  edgeId: string;
  dispositionOptionId: string;
  label: string;
  sourceFunnelId: string;
  onClose: () => void;
  onRenamed: (newLabel: string) => void;
  onDeleted: () => void;
}

const EdgePanel = ({
  dispositionOptionId,
  label,
  sourceFunnelId,
  onClose,
  onRenamed,
  onDeleted,
}: Props) => {
  const [name, setName] = useState(label);
  const [nameError, setNameError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleSave = async () => {
    const trimmed = capitalizeFirstLetter(name.trim());
    if (!trimmed || trimmed.length < 3) {
      setNameError('Nome deve ter ao menos 3 caracteres.');
      return;
    }
    setNameError('');
    setSaving(true);
    try {
      await api.put(`/disposition_option/${dispositionOptionId}`, { name: trimmed });
      toast.success('Tabulação renomeada!');
      onRenamed(trimmed);
    } catch (error) {
      toast.error('Erro ao renomear tabulação.');
      datadogRum.addError(new Error('Fail to rename edge disposition'), {
        error,
        dispositionOptionId,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/sales_funnel/${sourceFunnelId}/disposition/${dispositionOptionId}`);
      toast.success('Conexão removida!');
      onDeleted();
    } catch (error) {
      toast.error('Erro ao remover conexão.');
      datadogRum.addError(new Error('Fail to delete funnel edge'), {
        error,
        sourceFunnelId,
        dispositionOptionId,
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box
      position="absolute"
      top={0}
      right={0}
      w={300}
      h="100%"
      bg="grayscale.fg"
      borderLeft="1px solid"
      borderColor="border.muted"
      p={4}
      zIndex={10}
      overflowY="auto"
    >
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Text fontWeight={700} fontSize="md">Conexão</Text>
        <CloseButton size="sm" onClick={onClose} />
      </Flex>

      <Text fontSize="sm" fontWeight={500} mb={1}>Nome da tabulação</Text>
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        variant="subtle"
        size="sm"
        mb={1}
      />
      {nameError && <ErrorLabel text={nameError} />}

      <Button
        size="sm"
        bg="green.700"
        color="white"
        w="full"
        mt={2}
        onClick={handleSave}
        loading={saving}
      >
        Salvar nome
      </Button>

      <Button
        size="sm"
        colorPalette="red"
        variant="outline"
        w="full"
        mt={3}
        onClick={handleDelete}
        loading={deleting}
      >
        Remover conexão
      </Button>
    </Box>
  );
};

export default EdgePanel;
